import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { projectsProjectShippingAddressSchema } from '@crest/module-projects/contracts/project-order-reference';
import { z } from 'zod';
export const procurementOrdersConfigurationValueSchema = z.strictObject({
    maximumOrders: z.union([z.literal(1_000), z.literal(5_000), z.literal(10_000), z.literal(25_000)]),
    maximumLinesPerOrder: z.number().int().min(1).max(250),
    defaultCurrencyCode: z.string().regex(/^[A-Z]{3}$/),
    defaultTaxBasisPoints: z.number().int().min(0).max(10_000),
    defaultShippingAddress: projectsProjectShippingAddressSchema.nullable(),
    workOrderPrefix: z.string().regex(/^[A-Z][A-Z0-9-]{0,7}$/),
    purchaseOrderPrefix: z.string().regex(/^[A-Z][A-Z0-9-]{0,7}$/),
});
export const procurementOrdersConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'procurement.orders.configuration',
        moduleId: 'procurement',
        title: 'Procurement order behavior',
        helpText: 'Controls bounded order limits, exact defaults, tenant numbering prefixes, and the shop shipping fallback.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            maximumOrders: 10_000,
            maximumLinesPerOrder: 100,
            defaultCurrencyCode: 'USD',
            defaultTaxBasisPoints: 0,
            defaultShippingAddress: null,
            workOrderPrefix: 'WO',
            purchaseOrderPrefix: 'PO',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: procurementOrdersConfigurationValueSchema,
});
export const procurementOrdersDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showProjectNumber: z.boolean(),
    showCounterparty: z.boolean(),
    showCostsWhenPermitted: z.boolean(),
    projectPanelEnabled: z.boolean(),
});
export const procurementOrdersDisplaySetting = definePlatformSetting({
    definition: {
        key: 'procurement.orders.display',
        moduleId: 'procurement',
        title: 'Procurement order display',
        helpText: 'Controls order list density, card sizing, safe detail visibility, and the Project-hosted summary.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            pageSize: 50,
            density: 'comfortable',
            cardSize: 'standard',
            showProjectNumber: true,
            showCounterparty: true,
            showCostsWhenPermitted: true,
            projectPanelEnabled: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: procurementOrdersDisplayValueSchema,
});
const label = z.string().trim().min(1).max(160);
export const procurementOrdersCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(300),
    projectPanelTitle: label,
    projectPanelEmpty: z.string().trim().min(1).max(300),
    createWorkOrderAction: label,
    createPurchaseOrderAction: label,
    reviseAction: label,
    transitionAction: label,
    archiveAction: label,
    restoreAction: label,
    searchLabel: label,
    kindFilterLabel: label,
    statusFilterLabel: label,
    allKindsLabel: label,
    allStatusesLabel: label,
    includeArchivedLabel: label,
    orderNumberLabel: label,
    projectLabel: label,
    purchaserLabel: label,
    currentPurchaserLabel: label,
    allPurchasersLabel: label,
    statusLabel: label,
    openOrdersLabel: label,
    totalLabel: label,
    lineNameLabel: label,
    addLineAction: label,
    removeLineAction: label,
    cancelAction: label,
    savedLabel: label,
    createReason: z.string().trim().min(10).max(500),
    reviseReason: z.string().trim().min(10).max(500),
    counterpartyLabel: label,
    scopeLabel: label,
    notesLabel: label,
    quantityLabel: label,
    unitLabel: label,
    unitPriceLabel: label,
    taxLabel: label,
    shippingAddressLabel: label,
    expectedDeliveryLabel: label,
    scheduledDateLabel: label,
    reasonLabel: label,
    loadingLabel: label,
    savingLabel: label,
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    emptyTitle: label,
    emptyDescription: z.string().trim().min(1).max(300),
    permissionDenied: z.string().trim().min(1).max(240),
    kindLabels: z.strictObject({
        'subcontract-work-order': label,
        'purchase-order': label,
    }),
    statusLabels: z.strictObject({
        draft: label,
        sent: label,
        in_progress: label,
        completed: label,
        ordered: label,
        partial_received: label,
        received: label,
        cancelled: label,
    }),
});
export const procurementOrdersCopySetting = definePlatformSetting({
    definition: {
        key: 'procurement.copy.orders',
        moduleId: 'procurement',
        title: 'Procurement order copy',
        helpText: 'Controls tenant-editable order, work-order, purchase-order, status, filter, and Project-panel labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Procurement',
            title: 'Work & purchase orders',
            description: 'Create retained subcontract work orders and purchase orders with exact line totals and Project context.',
            projectPanelTitle: 'Procurement orders',
            projectPanelEmpty: 'No Procurement orders are linked to this Project.',
            createWorkOrderAction: 'Create work order',
            createPurchaseOrderAction: 'Create purchase order',
            reviseAction: 'Save order revision',
            transitionAction: 'Change status',
            archiveAction: 'Archive order',
            restoreAction: 'Restore order',
            searchLabel: 'Search orders',
            kindFilterLabel: 'Order type',
            statusFilterLabel: 'Status',
            allKindsLabel: 'All order types',
            allStatusesLabel: 'All statuses',
            includeArchivedLabel: 'Include archived orders',
            orderNumberLabel: 'Order number',
            projectLabel: 'Project',
            purchaserLabel: 'Purchaser',
            currentPurchaserLabel: 'Current CREST user',
            allPurchasersLabel: 'All purchasers',
            statusLabel: 'Status',
            openOrdersLabel: 'Open orders',
            totalLabel: 'Total',
            lineNameLabel: 'Line item',
            addLineAction: 'Add line',
            removeLineAction: 'Remove line',
            cancelAction: 'Cancel',
            savedLabel: 'Saved',
            createReason: 'Create this Procurement order',
            reviseReason: 'Revise this Procurement order with the current changes',
            counterpartyLabel: 'Vendor or subcontractor',
            scopeLabel: 'Scope or description',
            notesLabel: 'Internal notes',
            quantityLabel: 'Quantity',
            unitLabel: 'Unit',
            unitPriceLabel: 'Unit price',
            taxLabel: 'Tax rate',
            shippingAddressLabel: 'Shipping address',
            expectedDeliveryLabel: 'Expected delivery',
            scheduledDateLabel: 'Scheduled date',
            reasonLabel: 'Change reason',
            loadingLabel: 'Loading Procurement orders…',
            savingLabel: 'Saving Procurement order…',
            loadError: 'Procurement orders could not be loaded.',
            mutationError: 'The Procurement order change could not be saved. Refresh and try again.',
            emptyTitle: 'No orders found',
            emptyDescription: 'No Procurement orders match the current filters.',
            permissionDenied: 'You need procurement.orders.read-all to work with another purchaser’s shop order.',
            kindLabels: {
                'subcontract-work-order': 'Work order',
                'purchase-order': 'Purchase order',
            },
            statusLabels: {
                draft: 'Draft',
                sent: 'Sent',
                in_progress: 'In progress',
                completed: 'Completed',
                ordered: 'Ordered',
                partial_received: 'Partially received',
                received: 'Received',
                cancelled: 'Cancelled',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: procurementOrdersCopyValueSchema,
});
export const procurementSupplierOperationsConfigurationValueSchema = z.strictObject({
    maximumSubcontracts: z.union([z.literal(1_000), z.literal(5_000), z.literal(10_000), z.literal(25_000)]),
    maximumFulfillments: z.union([z.literal(1_000), z.literal(5_000), z.literal(10_000), z.literal(25_000)]),
    maximumReceivingRecords: z.union([z.literal(1_000), z.literal(5_000), z.literal(10_000), z.literal(25_000)]),
    maximumLinesPerRecord: z.number().int().min(1).max(250),
    requireProofForVerifiedReceiving: z.boolean(),
});
export const procurementSupplierOperationsConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'procurement.supplier-operations.configuration',
        moduleId: 'procurement',
        title: 'Procurement supplier operations behavior',
        helpText: 'Controls bounded subcontract, fulfillment, and receiving limits plus proof requirements for verified receipts.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            maximumSubcontracts: 5_000,
            maximumFulfillments: 5_000,
            maximumReceivingRecords: 10_000,
            maximumLinesPerRecord: 100,
            requireProofForVerifiedReceiving: false,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: procurementSupplierOperationsConfigurationValueSchema,
});
export const procurementSupplierOperationsDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    defaultView: z.enum(['subcontracts', 'fulfillments', 'receiving']),
    showCostsWhenPermitted: z.boolean(),
    showFulfillmentEvents: z.boolean(),
    projectPanelEnabled: z.boolean(),
});
export const procurementSupplierOperationsDisplaySetting = definePlatformSetting({
    definition: {
        key: 'procurement.supplier-operations.display',
        moduleId: 'procurement',
        title: 'Procurement supplier operations display',
        helpText: 'Controls page size, card dimensions, density, default workspace, cost visibility, and Project summaries.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            pageSize: 50,
            density: 'comfortable',
            cardSize: 'standard',
            defaultView: 'subcontracts',
            showCostsWhenPermitted: true,
            showFulfillmentEvents: true,
            projectPanelEnabled: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: procurementSupplierOperationsDisplayValueSchema,
});
export const procurementSupplierOperationsCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(300),
    subcontractsLabel: label,
    fulfillmentsLabel: label,
    receivingLabel: label,
    createSubcontractAction: label,
    createFulfillmentAction: label,
    createReceivingAction: label,
    archiveAction: label,
    restoreAction: label,
    searchLabel: label,
    statusLabel: label,
    projectLabel: label,
    supplierLabel: label,
    subcontractorLabel: label,
    tradeLabel: label,
    progressLabel: label,
    deliveryLabel: label,
    pickupLabel: label,
    receiptLabel: label,
    shortageLabel: label,
    costLabel: label,
    loadingLabel: label,
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    emptyTitle: label,
    emptyDescription: z.string().trim().min(1).max(300),
    projectPanelTitle: label,
    projectPanelEmpty: z.string().trim().min(1).max(300),
});
export const procurementSupplierOperationsCopySetting = definePlatformSetting({
    definition: {
        key: 'procurement.copy.supplier-operations',
        moduleId: 'procurement',
        title: 'Procurement supplier operations copy',
        helpText: 'Controls tenant-editable subcontract, supplier fulfillment, receiving, shortage, cost, and Project-panel labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Supplier operations',
            title: 'Subcontracts, fulfillment & receiving',
            description: 'Manage subcontract progress, supplier delivery and pickup events, packing slips, shortages, and actual receiving costs.',
            subcontractsLabel: 'Subcontracts',
            fulfillmentsLabel: 'Supplier fulfillment',
            receivingLabel: 'Receiving',
            createSubcontractAction: 'Add subcontract',
            createFulfillmentAction: 'Add supplier order',
            createReceivingAction: 'Record delivery or pickup',
            archiveAction: 'Archive',
            restoreAction: 'Restore',
            searchLabel: 'Search supplier operations',
            statusLabel: 'Status',
            projectLabel: 'Project',
            supplierLabel: 'Supplier',
            subcontractorLabel: 'Subcontractor',
            tradeLabel: 'Trade',
            progressLabel: 'Progress',
            deliveryLabel: 'Delivery',
            pickupLabel: 'Pickup',
            receiptLabel: 'Receipt or proof',
            shortageLabel: 'Shortage',
            costLabel: 'Cost',
            loadingLabel: 'Loading Procurement supplier operations…',
            loadError: 'Procurement supplier operations could not be loaded.',
            mutationError: 'The Procurement supplier operation could not be saved. Refresh and try again.',
            emptyTitle: 'No supplier records found',
            emptyDescription: 'No subcontract, fulfillment, or receiving records match the current view.',
            projectPanelTitle: 'Supplier operations',
            projectPanelEmpty: 'No Procurement supplier operations are linked to this Project.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: procurementSupplierOperationsCopyValueSchema,
});
export const procurementSettingRegistrations = [
    procurementOrdersConfigurationSetting,
    procurementOrdersDisplaySetting,
    procurementOrdersCopySetting,
    procurementSupplierOperationsConfigurationSetting,
    procurementSupplierOperationsDisplaySetting,
    procurementSupplierOperationsCopySetting,
];
//# sourceMappingURL=procurement-setting-definitions.js.map